import { z } from "zod";

import { filedAmountSchema } from "./document-filing";
import type { DocumentTypeId } from "./document-types";
import { documentPaymentSchema, type FinanceAuditEvent } from "./reconciliation";

// Payables and receivables: the bills and claims the organization has to pay and the invoices it issued that it is
// owed, as Document Filing read them from its Intake documents, and where each one stands — paid, which reconciliation
// says by matching a bank transaction to it, settled by hand, or still to be paid. Only the documents of the current
// year are followed, the year in the organization's own time zone (Settings › Identity), as reconciliation reads the
// current year's bank transactions alone. The API and the frontend share these.

/** Which way a followed document's money goes: out of the organization, or into it. */
export const PAYMENT_SIDES = ["payables", "receivables"] as const;
export type PaymentSide = (typeof PAYMENT_SIDES)[number];

/**
 * The kinds of document each side follows, in payments' own words: a vendor's invoice and a reimbursement claim are the
 * organization's to pay, and an invoice it issued is a customer's. Each is one type of the document type catalogue
 * (`paymentKindOfType`); every other type — a receipt, paid when it was made; a credit note; a remittance; a contract —
 * is not paid as a bill is, and is not followed.
 */
export const PAYMENT_SIDE_KINDS = {
	payables: ["vendor-invoice", "reimbursement-claim"],
	receivables: ["customer-invoice"],
} as const satisfies Readonly<Record<PaymentSide, readonly string[]>>;

/** Every kind a side follows. */
export const PAYMENT_DOCUMENT_KINDS = [...PAYMENT_SIDE_KINDS.payables, ...PAYMENT_SIDE_KINDS.receivables] as const;
export type PaymentDocumentKind = (typeof PAYMENT_DOCUMENT_KINDS)[number];

/** The type of the catalogue each followed kind is. */
const PAYMENT_KIND_TYPES: Readonly<Record<PaymentDocumentKind, DocumentTypeId>> = {
	"vendor-invoice": "fin.ap.vendor_invoice",
	"reimbursement-claim": "fin.ap.reimbursement_claim",
	"customer-invoice": "fin.ar.customer_invoice",
};

/**
 * The kind a side follows a document of `type` as, or undefined for a type no side follows — and for a document with
 * no type, out of scope or not classified yet, which no side follows either.
 */
export function paymentKindOfType(type: DocumentTypeId | undefined): PaymentDocumentKind | undefined {
	return PAYMENT_DOCUMENT_KINDS.find(kind => PAYMENT_KIND_TYPES[kind] === type);
}

/** What each followed kind is called in a sentence: an audit entry, a toast. */
export const PAYMENT_DOCUMENT_NOUNS: Readonly<Record<PaymentDocumentKind, string>> = {
	"vendor-invoice": "Bill",
	"reimbursement-claim": "Reimbursement claim",
	"customer-invoice": "Invoice",
};

/** The side a followed kind is on. */
export function paymentSideOf(kind: PaymentDocumentKind): PaymentSide {
	return kind === "customer-invoice" ? "receivables" : "payables";
}

/**
 * Where a followed document stands, worked out whenever it is read, never stored:
 *
 * - `overdue` — nothing pays or settles it, and the day it is due has passed;
 * - `open` — nothing pays or settles it, and it is due today or later, or says no day it is due;
 * - `paid` — reconciliation has matched a bank transaction to it, in this year or an earlier one;
 * - `settled` — a person said it was settled another way, and why.
 *
 * `paid` comes first: a document a person settled is paid once a transaction is matched to it.
 */
export const PAYMENT_STATUSES = ["overdue", "open", "paid", "settled"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABELS: Readonly<Record<PaymentStatus, string>> = {
	overdue: "Overdue",
	open: "Open",
	paid: "Paid",
	settled: "Settled",
};

/** Why a person settled a document by hand. */
export const SETTLEMENT_REASONS = ["paid-elsewhere", "credit-note", "written-off", "nothing-due", "other"] as const;
export type SettlementReason = (typeof SETTLEMENT_REASONS)[number];

export const SETTLEMENT_REASON_LABELS: Readonly<Record<SettlementReason, string>> = {
	"paid-elsewhere": "Paid another way",
	"credit-note": "Settled by a credit note",
	"written-off": "Written off",
	"nothing-due": "Nothing to pay",
	other: "Other",
};

/** At most this many characters of a note on why a document is settled. */
export const SETTLEMENT_NOTE_MAX_LENGTH = 500;

/** What settles a document by hand: why, with what note, who said so and when. */
export const settlementSchema = z.strictObject({
	reason: z.enum(SETTLEMENT_REASONS),
	note: z.string().min(1).max(SETTLEMENT_NOTE_MAX_LENGTH).optional(),
	by: z.string().min(1).max(80),
	/** When, as an ISO 8601 timestamp in UTC. */
	at: z.iso.datetime(),
});
export type Settlement = z.infer<typeof settlementSchema>;

/** One followed document: what Document Filing read of it, and where it stands. */
export const paymentDocumentSchema = z.strictObject({
	fileId: z.string().min(1).max(128),
	fileName: z.string().min(1).max(255),
	kind: z.enum(PAYMENT_DOCUMENT_KINDS),
	/** Who it is with — the vendor, the customer, the person who claims — as the document names them. */
	counterparty: z.string().min(1).max(160).optional(),
	number: z.string().min(1).max(80).optional(),
	issuedOn: z.iso.date().optional(),
	dueOn: z.iso.date().optional(),
	/** What it comes to, all in, in the currency it states: always more than nothing. */
	total: filedAmountSchema,
	status: z.enum(PAYMENT_STATUSES),
	/** How many bank transactions are matched to it, in any year. */
	payments: z.int().nonnegative(),
	/** The day the latest of them was booked, in the organization's time zone; absent while there is none. */
	paidOn: z.iso.date().optional(),
	/** What settles it by hand, while something does. */
	settlement: settlementSchema.optional(),
});
export type PaymentDocument = z.infer<typeof paymentDocumentSchema>;

/** How many of a side's documents stand in each status, every status counted, zero included. */
export const paymentCountsSchema = z.record(z.enum(PAYMENT_STATUSES), z.int().nonnegative());
export type PaymentCounts = z.infer<typeof paymentCountsSchema>;

/** What several documents come to in one currency: a decimal string, never a float, so no cent is lost adding up. */
export const paymentSumSchema = z.strictObject({
	amount: z.string().regex(/^\d{1,16}(?:\.\d{1,5})?$/, "Invalid amount"),
	currency: z.string().regex(/^[A-Z]{3}$/, "Invalid currency"),
	/** How many documents it is the sum of. */
	documents: z.int().positive(),
});
export type PaymentSum = z.infer<typeof paymentSumSchema>;

/** What a side's documents still to be paid come to: every open and overdue one, per kind and currency. */
export const outstandingSumSchema = paymentSumSchema.extend({ kind: z.enum(PAYMENT_DOCUMENT_KINDS) });
export type OutstandingSum = z.infer<typeof outstandingSumSchema>;

/** One side of the year in figures: each status counted, what is still to be paid, and what of it is overdue. */
export const paymentSideSummarySchema = z.strictObject({
	counts: paymentCountsSchema,
	/** Per kind and currency, the kinds in `PAYMENT_SIDE_KINDS`'s order, then by currency. */
	outstanding: z.array(outstandingSumSchema),
	/** Per currency, by currency. */
	overdue: z.array(paymentSumSchema),
});
export type PaymentSideSummary = z.infer<typeof paymentSideSummarySchema>;

/** What `GET /api/payments` answers: both sides of the current year in figures, as Finance's overview shows them. */
export const paymentsSummarySchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	payables: paymentSideSummarySchema,
	receivables: paymentSideSummarySchema,
});
export type PaymentsSummary = z.infer<typeof paymentsSummarySchema>;

/** The Payables and Receivables pages list this many documents a page. */
export const PAYMENTS_PAGE_SIZE = 25;

/** A link to another page of a side. The API makes it; a client passes it back unchanged and never reads it. */
export const paymentsPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/** The query of `GET /api/payments/payables` and `/receivables`: one status, or every one, and the page to show. */
export const paymentsQuerySchema = z.object({
	status: z.enum(PAYMENT_STATUSES).optional(),
	page: paymentsPageTokenSchema.optional(),
});
export type PaymentsQuery = z.infer<typeof paymentsQuerySchema>;

/**
 * One page of a side's documents of the year: the overdue first, the longest overdue first; then the open, the soonest
 * due first; then the paid and the settled, the latest due first — each by the day it is due, or dated when it says no
 * day it is due — with every status counted and links to the pages beside it: `null` where there is none.
 */
export const paymentsPageSchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	side: z.enum(PAYMENT_SIDES),
	documents: z.array(paymentDocumentSchema).max(PAYMENTS_PAGE_SIZE),
	/** How many documents of the status asked for, or of every status, the side holds, every page counted. */
	total: z.int().nonnegative(),
	counts: paymentCountsSchema,
	next: paymentsPageTokenSchema.nullable(),
	previous: paymentsPageTokenSchema.nullable(),
});
export type PaymentsPage = z.infer<typeof paymentsPageSchema>;

/**
 * What `GET /api/payments/documents/{fileId}` answers: one followed document, and every bank transaction matched to it,
 * the latest booked first.
 */
export const paymentDocumentDetailSchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	document: paymentDocumentSchema,
	payments: z.array(documentPaymentSchema),
});
export type PaymentDocumentDetail = z.infer<typeof paymentDocumentDetailSchema>;

/** What `PUT /api/payments/documents/{fileId}/settlement` takes: why the document is settled, and a note if any. */
export const settlementRequestSchema = z.strictObject({
	reason: z.enum(SETTLEMENT_REASONS),
	note: z.string().trim().max(SETTLEMENT_NOTE_MAX_LENGTH).optional(),
});
export type SettlementRequest = z.infer<typeof settlementRequestSchema>;

/** What the routes that settle a document, and reopen one, answer: the document as it now stands. */
export const paymentDocumentAnswerSchema = z.strictObject({ document: paymentDocumentSchema });
export type PaymentDocumentAnswer = z.infer<typeof paymentDocumentAnswerSchema>;

/** What an audit entry names a document by: its name, and what it comes to. */
interface AuditedDocument {
	readonly kind: PaymentDocumentKind;
	readonly fileName: string;
	readonly total: { readonly amount: string; readonly currency: string };
}

function documentNamed({ fileName, total }: AuditedDocument): string {
	return `${fileName} · ${total.amount} ${total.currency}`;
}

/**
 * "Bill settled" / "invoice-vantage-cloud-annual.pdf · 2880.00 EUR · Paid another way"; a note's contents are never
 * among it.
 */
export function documentSettledAudit(document: AuditedDocument, reason: SettlementReason): FinanceAuditEvent {
	return {
		action: `${PAYMENT_DOCUMENT_NOUNS[document.kind]} settled`,
		detail: `${documentNamed(document)} · ${SETTLEMENT_REASON_LABELS[reason]}`,
	};
}

/** "Invoice reopened" / "invoice-beacon-retail.pdf · 12400.00 GBP": what settled it is taken back. */
export function documentReopenedAudit(document: AuditedDocument): FinanceAuditEvent {
	return { action: `${PAYMENT_DOCUMENT_NOUNS[document.kind]} reopened`, detail: documentNamed(document) };
}
